import React, {
    Dispatch,
    KeyboardEvent,
    ReactNode,
    SetStateAction,
} from 'react';

import { IconTrqkMark, IconUpArrow, IconX } from '..';

/**
 * @typedef {StickyTabProps}
 * @property {boolean} cardIsOpen whether the card is open
 * @property {ReactNode} children
 * @property {Dispatch<SetStateAction<boolean>>} setCardIsOpen setState function for whether card is
 * open
 */
interface StickyTabProps {
    cardIsOpen: boolean;
    children: ReactNode;
    setCardIsOpen: Dispatch<SetStateAction<boolean>>;
}

const width = 580;

/**
 * A card with a purple background that sticks to the lower right corner of the page. The card can
 * be expanded or closed. When in a closed state, the top of the card remains visible, like a file
 * folder tab in a drawer.
 * @param props {StickyTabProps}
 * @returns {JSX.Element}
 */
export const StickyTab = ({
    cardIsOpen,
    children,
    setCardIsOpen,
}: StickyTabProps): JSX.Element => (
    <div className="fixed flex justify-end items-end right-0 bottom-0">
        <div className="flex-initial" style={{ width }}>
            {cardIsOpen && (
                <div className="py-8 mr-12 bg-primary-500 text-white rounded-t-lg">
                    <div className="flex justify-between">
                        <div className="flex-grow pl-12 pr-2">
                            <div className="pb-3">
                                <IconTrqkMark
                                    width={30}
                                    fillColor="secondary-500"
                                />
                            </div>
                            {children}
                        </div>
                        <div className="pr-6">
                            <IconX
                                width={20}
                                fillColor="primary-300"
                                handleClick={(): void => {
                                    setCardIsOpen(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
            {!cardIsOpen && (
                <div
                    className="pl-8 py-2 mr-12 bg-primary-500 text-white rounded-t-lg"
                    onClick={(): void => {
                        setCardIsOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: KeyboardEvent): void => {
                        if (e.key === 'Enter') {
                            setCardIsOpen(true);
                        }
                    }}
                >
                    <div className="flex justify-between">
                        <div className="px-4 pt-3">
                            <IconTrqkMark
                                width={30}
                                fillColor="secondary-500"
                            />
                        </div>
                        <div className="pr-4">
                            <IconUpArrow height={45} fillColor="primary-300" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
);
